import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { useDispatch, useSelector } from "react-redux";
import { CandidateProfile } from "../../../Redux/action/CandidateProfileAction";
import { CandidateCertificationAction } from "../../../Redux/action/CandidateCertificationAction";
import Swal from "sweetalert2";
import axios from "axios";
import { toast } from "react-toastify";
import ViewCertificate from "./ViewCertificate";

const Certifications = () => {
  const dispatch = useDispatch();
  const { userResult } = useSelector((state) => state.Auth);
  const certificates = useSelector(
    (state) => state?.Profile?.result?.candidateCertificate
  );

  const candidateId = userResult?.userid;
  const token = userResult?.token || localStorage.getItem("token");
  const baseUrl = process.env.REACT_APP_API_PATH;

  const [loading, setLoading] = useState(false);
  const [viewCer, setViewCer] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [certificateId, setCertificateId] = useState("");
  const [isDeleteImage, setIsDeleteImage] = useState(false);

  const [certificateData, setCertificateData] = useState({
    courseName: "",
    duration: "",
    durationUnit: "",
    certificationId: "",
    certificationUrl: "",
    year: "",
    courseProvider: "",
  });

  useEffect(() => {
    dispatch(CandidateProfile(candidateId));
  }, []);

  /* ================= HANDLERS ================= */

  const handleChange = (e) => {
    setCertificateData({
      ...certificateData,
      [e.target.name]: e.target.value,
    });
  };

  const handleImageChange = (e) => {
    setSelectedImage(null);
    const file = e.target.files[0];
    if (!file) return;

    const MAX_SIZE = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > MAX_SIZE) {
      toast.error(
        "Please upload a file greater than 4KB and smaller than 2MB."
      );
      return;
    }
    setSelectedImage(file);
  };

  const handleAddCertification = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!certificateData.courseName?.trim()) {
      toast.error("Course name is required");
      return;
    }

    if (!/^[A-Za-z\s.+#]+$/.test(certificateData.courseName.trim())) {
      toast.error(
        "Course name can only contain letters, spaces, and symbols like . + #"
      );
      return;
    }

    if (!certificateData.duration || !certificateData.durationUnit) {
      toast.error("Please select duration and duration unit");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();

      formData.append(
        "candidateCertificateRequest",
        JSON.stringify({
          candidateId,
          ...certificateData,
          duration: `${certificateData.duration} ${certificateData.durationUnit}`,
        })
      );

      if (selectedImage) {
        formData.append("image", selectedImage);
      }

      const resp = await dispatch(CandidateCertificationAction(formData));

      if (resp?.data?.status?.success === false) {
        throw new Error(resp?.data?.status?.message || "Add failed");
      }

      await dispatch(CandidateProfile(candidateId));

      setIsModalOpen(false);
      setSelectedImage(null);
      toast.success("Certificate added successfully");
    } catch (error) {
      console.error("Add certification error:", error);

      toast.error(
        error?.response?.data?.status?.message ||
          error?.message ||
          "Failed to add certificate"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCertification = async (e) => {
    e.preventDefault();

    if (!certificateData.courseName?.trim()) {
      toast.error("Course name is required");
      return;
    }

    setLoading(true);
    setSelectedImage(null);

    try {
      const formData = new FormData();

      formData.append(
        "candidateCertificateRequest",
        JSON.stringify({
          ...certificateData,
          certificateId,
          isDeleteImage,
          duration: `${certificateData.duration} ${certificateData.durationUnit}`,
        })
      );

      if (selectedImage) {
        formData.append("image", selectedImage);
      }

      const resp = await axios.put(
        `${baseUrl}/ebench/update_certificate`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Update response:", resp?.data);

      if (resp?.data?.status?.success === false) {
        throw new Error(resp?.data?.status?.message || "Update failed");
      }

      await dispatch(CandidateProfile(candidateId));

      setIsEditModalOpen(false);
      toast.success("Certificate updated successfully");
    } catch (error) {
      console.error("Update certification error:", error);

      toast.error(
        error?.response?.data?.status?.message ||
          error?.message ||
          "Failed to update certificate"
      );
    } finally {
      setLoading(false);
      setSelectedImage(null);
    }
  };

  const removeImage = () => {
    setIsDeleteImage(true);
    setSelectedImage(null);
  };

  const resetForm = () => {
    setCertificateData({
      courseName: "",
      duration: "",
      durationUnit: "",
      certificationId: "",
      certificationUrl: "",
      year: "",
      courseProvider: "",
    });
    setSelectedImage(null);
  };

  const years = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: currentYear - 1970 + 1 }, (_, i) => 1970 + i);
  }, []);

  const parseDuration = (durationStr = "") => {
    if (!durationStr) return { duration: "", durationUnit: "" };

    const parts = durationStr.split(" ");
    return {
      duration: parts[0] || "",
      durationUnit: parts[1] || "",
    };
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Delete Certificate?",
      showCancelButton: true,
      confirmButtonColor: "#286a94",
    }).then(async (res) => {
      if (res.isConfirmed) {
        await axios.delete(
          `${baseUrl}/ebench/delete_certificate?certificateId=${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        toast.success("Deleted successfully");
        dispatch(CandidateProfile(candidateId));
      }
    });
  };

  /* ================= UI ================= */

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.3)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {!viewCer && (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6" fontWeight={700} color="#286a94">
                Certifications
              </Typography>

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setIsModalOpen(true);
                  resetForm();
                }}
                sx={{ borderColor: "#286a94", color: "#286a94" }}
              >
                Add Certification
              </Button>
            </Box>

            <TableContainer>
              <Table>
                <TableHead sx={{ background: "#f5f9fc" }}>
                  <TableRow>
                    <TableCell>S.No</TableCell>
                    <TableCell>Course Name</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Certification ID</TableCell>
                    <TableCell align="center">View</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!certificates?.length ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No certificates available
                      </TableCell>
                    </TableRow>
                  ) : (
                    certificates?.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>{item.courseName}</TableCell>
                        <TableCell>{item.duration}</TableCell>
                        <TableCell>{item.certificationId}</TableCell>

                        <TableCell align="center">
                          <IconButton
                            onClick={() => {
                              setViewCer(true);
                              setCertificateData(item);
                            }}
                          >
                            <VisibilityIcon sx={{ color: "#286a94" }} />
                          </IconButton>
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton
                              onClick={() => {
                                setCertificateId(item.certificateId);
                                const { duration, durationUnit } =
                                  parseDuration(item.duration);
                                setCertificateData({
                                  candidateId: item.candidateId,
                                  courseName: item.courseName,
                                  duration,
                                  durationUnit,
                                  certificationId: item.certificationId,
                                  certificationUrl: item.certificationUrl,
                                  year: item.year,
                                  courseProvider: item.courseProvider,
                                });
                                setIsEditModalOpen(true);
                              }}
                            >
                              <EditIcon sx={{ color: "#286a94" }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete">
                            <IconButton
                              onClick={() => handleDelete(item.certificateId)}
                            >
                              <DeleteIcon color="error" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {viewCer && (
        <ViewCertificate
          viewCer={viewCer}
          setViewCer={setViewCer}
          data={certificateData}
        />
      )}

      {/* ADD MODAL */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#286a94" }}>
          Add Certification
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2} mt={0.5}>
            {/* Course Name */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="Course Name"
                name="courseName"
                value={certificateData.courseName}
                onChange={handleChange}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>

            {/* Duration */}
            <Grid item xs={12} sm={4}>
              <Grid item xs={12} sm={12}>
                <TextField
                  select
                  fullWidth
                  required
                  label="Duration"
                  name="duration"
                  value={certificateData.duration}
                  onChange={handleChange}
                  sx={{
                    width: "100%",
                  }}
                >
                  {[...Array(12)].map((_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {i + 1}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {/* Duration Unit */}
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                required
                label="Duration Unit"
                name="durationUnit"
                value={certificateData.durationUnit}
                onChange={handleChange}
              >
                <MenuItem value="Weeks">Weeks</MenuItem>
                <MenuItem value="Months">Months</MenuItem>
                <MenuItem value="Years">Years</MenuItem>
              </TextField>
            </Grid>

            {/* Certification ID */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="Certification ID"
                name="certificationId"
                value={certificateData.certificationId}
                onChange={handleChange}
                inputProps={{
                  maxLength: 25,
                  pattern: "^[a-zA-Z0-9]+$",
                }}
                helperText="Alphanumeric only"
              />
            </Grid>

            {/* Completion Year */}
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                required
                label="Completion Year"
                name="year"
                value={certificateData.year}
                onChange={handleChange}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Certification URL */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Certification URL"
                name="certificationUrl"
                value={certificateData.certificationUrl}
                onChange={handleChange}
                placeholder="https://example.com"
                inputProps={{
                  maxLength: 250,
                  pattern: "https://.*",
                }}
                helperText="Must start with https://"
              />
            </Grid>

            {/* Course Provider */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Course Provider"
                name="courseProvider"
                value={certificateData.courseProvider}
                onChange={handleChange}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>

            {/* Image Upload */}
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{
                  height: 56,
                  borderColor: "#cbd5e1",
                  color: "#475569",
                }}
              >
                Upload Certificate Image
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>
            </Grid>

            {/* Image Preview */}
            {selectedImage && (
              <Grid item xs={12}>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={1}
                >
                  <img
                    src={URL.createObjectURL(selectedImage)}
                    alt="certificate"
                    style={{ width: 200, borderRadius: 8 }}
                  />
                  <Button size="small" color="error" onClick={removeImage}>
                    Remove Image
                  </Button>
                </Box>
              </Grid>
            )}

            {/* Info */}
            <Grid item xs={12}>
              <Typography variant="caption" color="primary">
                Please upload a file larger than 4 KB and smaller than 2 MB.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setIsModalOpen(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddCertification}
            sx={{
              bgcolor: "#286a94",
              "&:hover": { bgcolor: "#1f5577" },
            }}
          >
            Add Certification
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#286a94" }}>
          Update Certification
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2} mt={0.5}>
            {/* Course Name */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="Course Name"
                name="courseName"
                value={certificateData.courseName}
                onChange={handleChange}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>

            {/* Duration */}
            <Grid item xs={12} sm={4}>
              <Grid item xs={12} sm={12}>
                <TextField
                  select
                  fullWidth
                  required
                  label="Duration"
                  name="duration"
                  value={certificateData.duration}
                  onChange={handleChange}
                  sx={{
                    width: "100%",
                  }}
                >
                  {[...Array(12)].map((_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {i + 1}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {/* Duration Unit */}
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                required
                label="Duration Unit"
                name="durationUnit"
                value={certificateData.durationUnit}
                onChange={handleChange}
              >
                <MenuItem value="Weeks">Weeks</MenuItem>
                <MenuItem value="Months">Months</MenuItem>
                <MenuItem value="Years">Years</MenuItem>
              </TextField>
            </Grid>

            {/* Certification ID */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="Certification ID"
                name="certificationId"
                value={certificateData.certificationId}
                onChange={handleChange}
                inputProps={{
                  maxLength: 25,
                  pattern: "^[a-zA-Z0-9]+$",
                }}
                helperText="Alphanumeric only"
              />
            </Grid>

            {/* Completion Year */}
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                required
                label="Completion Year"
                name="year"
                value={certificateData.year}
                onChange={handleChange}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Certification URL */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Certification URL"
                name="certificationUrl"
                value={certificateData.certificationUrl}
                onChange={handleChange}
                placeholder="https://example.com"
                inputProps={{
                  maxLength: 250,
                  pattern: "https://.*",
                }}
                helperText="Must start with https://"
              />
            </Grid>

            {/* Course Provider */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Course Provider"
                name="courseProvider"
                value={certificateData.courseProvider}
                onChange={handleChange}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>

            {/* Image Upload */}
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{
                  height: 56,
                  borderColor: "#cbd5e1",
                  color: "#475569",
                }}
              >
                Upload Certificate Image
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>
            </Grid>

            {/* Image Preview */}
            {selectedImage && (
              <Grid item xs={12}>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={1}
                >
                  <img
                    src={
                      typeof selectedImage === "string"
                        ? selectedImage
                        : URL.createObjectURL(selectedImage)
                    }
                    alt="certificate"
                    style={{ width: 200, borderRadius: 8 }}
                  />

                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      setIsDeleteImage(true);
                      setSelectedImage(null);
                    }}
                  >
                    Remove Image
                  </Button>
                </Box>
              </Grid>
            )}

            {/* Info */}
            <Grid item xs={12}>
              <Typography variant="caption" color="primary">
                Please upload a file larger than 4 KB and smaller than 2 MB.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateCertification}
            sx={{
              bgcolor: "#286a94",
              "&:hover": { bgcolor: "#1f5577" },
            }}
          >
            Update Certification
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Certifications;
